import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Spin,
    TableView,
    TableViewColumn,
    TableViewRow,
    Tag,
} from "../../../design-system";
import { createOrganization, listOrganizations } from "../../../common/api/AdminDashboardClient";
import {
    CreateOrganizationRequest,
    Organization,
    OrganizationStatus,
} from "../../../types/AdminDashboardTypes";
import { handleError } from "../../../common/ErrorHandling";
import { formatDateMonthName } from "../../date/DateOp";
import { CreateOrganizationModal } from "./CreateOrganizationModal";
import classes from "./OrganizationsView.module.less";

const columns: TableViewColumn[] = [
    { key: "name", label: "Name" },
    { key: "domain", label: "Wristband tenant" },
    { key: "users", label: "Users" },
    { key: "projects", label: "Projects" },
    { key: "memberSince", label: "Member since" },
    { key: "status", label: "Status" },
];

export const OrganizationsView: React.FC = () => {
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState<Organization[] | null>(null);
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        listOrganizations().then(setOrganizations).catch(handleError);
    }, []);

    const create = async (request: CreateOrganizationRequest) => {
        const org = await createOrganization(request);
        navigate(`/superadmin/dashboard/organizations/${org.id}`);
    };

    const orgRow = (org: Organization): TableViewRow => ({
        key: String(org.id),
        searchText: org.name,
        values: {
            name: org.name,
            domain: org.domain,
            users: org.userCount,
            projects: org.projectCount,
            memberSince: formatDateMonthName(org.recordCreationDate),
            status:
                org.status === OrganizationStatus.ACTIVE ? (
                    <Tag color="green">Active</Tag>
                ) : (
                    <Tag color="red">Inactive</Tag>
                ),
        },
    });

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <h1 className={classes.title}>Organizations</h1>
                <Button type="primary" onClick={() => setCreating(true)}>
                    + Add organization
                </Button>
            </div>
            <CreateOrganizationModal
                open={creating}
                onCancel={() => setCreating(false)}
                onCreate={create}
            />
            {organizations === null ? (
                <Spin />
            ) : (
                <TableView
                    columns={columns}
                    rows={organizations.map(orgRow)}
                    searchPlaceholder="Search organizations"
                    onRowClick={(row) => navigate(`/superadmin/dashboard/organizations/${row.key}`)}
                />
            )}
        </div>
    );
};
